import { Button, Chip, DateSpan, FileItem, GenericSuspense, Metadata, Avatar, useFileUrl, useTranslationRoute, useTranslations, useUrlParamsValueToString, useRuntimeEnv, useUserProfilePicture } from "@bsm/component"
import { RefuseDayOffDialog } from "./RefuseDayOffDialog"
import { TTimeOff } from "@bsm/graphql"
import { Link, Outlet, useParams } from "react-router-dom"
import { ReactNode, useCallback, useMemo, useState } from "react"
import { useValidateDayOffMutation } from "hooks/useValidateDayOffMutation"
import { useRefuseDayOffMutation } from "hooks/useRefuseDayOffMutation"
import { useAppointmentDetailsEditLink } from "hooks/useAppointmentDetailsEditLink"
import { useExportDayOffMutation } from "hooks/useExportDayOffMutation"
import { AppointmentDetailsType } from "./AppointmentDetailsType"
import { useTimeOffQuery } from "hooks/useTimeOffQuery"
import { VacationStatus } from "./VacationStatus"
import { useContractManagerQuery } from "hooks/useContractManagerQuery"
import moment from "moment"
import { useQueryClient } from "react-query"
import { useTimeOffInvalidateQuery } from "hooks/useTimeOffInvalidateQuery"
import { useDailyQuotaLeaveTypes } from "hooks/useDailyQuotaLeaveTypes"

export const AppointmentDetails = (props: Props) => {
  const [
    CREATOR,
    STATUS,
    CLOSED,
    OPENED,
    CREATED_AT,
    DEMAND_DATE,
    DATE_BEGIN,
    DATE_END,
    LABELS,
    USERS,
    DESCRIPTION,
    ATTACHMENT,
    METADATA,
    ARCHIVED,
    EDIT,
    MILESTONE,
    USER,
    REPETITION,
    VALIDATE,
    APPROVE,
    REFUSE,
    PRINT,
    EMPLOYEE,
    POSITION,
    REMAINDER,
    TOTAL_DAYS,
    REASON,
    REFUSAL_REASON,
    EMPLOYEE_DETAILS,
    TOTAL_HOURS,
    ANNUAL_REMAINING_QUOTA
    ] = useTranslations(i18n)
    const { id } = useParams()
    const { invalidateQuery } = useTimeOffQuery(parseInt(id), true, props.disableDetailsQuery !== true);
    const invalidateTimeOffsQuery = useTimeOffInvalidateQuery()
    const editLink = useAppointmentDetailsEditLink(props.type)
    const fileUrl = useFileUrl()
    const tr = useTranslationRoute()
    const exportDayOff = useExportDayOffMutation()
    const { runtimeEnv } = useRuntimeEnv()

    const { data, invalidateQuery: invalidateContractQuery } = useContractManagerQuery(props?.employee?.id, props?.type?.designation as any, true);
    const queryClient = useQueryClient()
    const [isProcessingApprove, setIsProcessingApprove] = useState(false)
    const [isProcessingRefuse, setIsProcessingRefuse] = useState(false)
    const [refuseDialogOpen, setRefuseDialogOpen] = useState(false)

    const validateMutation = useValidateDayOffMutation()
    const refuseMutation = useRefuseDayOffMutation()
    const userProfilePictureUrl = useUserProfilePicture()

    const remainingSolde = (data?.leaveBalance as any)?.congeSolde?.toString()

    const dailyQuotaTypes = useDailyQuotaLeaveTypes()

    const handleApproveOrValidate = () => {
        if (!props?.id) return
        setIsProcessingApprove(true)
        validateMutation.mutate(props.id, {
            onSuccess: () => { 
              invalidateQuery();
              invalidateTimeOffsQuery();
              queryClient.invalidateQueries('App.bo.ContractManagerQuery');
            },
            onSettled: () => setIsProcessingApprove(false)
        })
    }

  const handleRefuse = () => {
        if (!props?.id) return
        setRefuseDialogOpen(true)
    }

    const handleRefuseConfirm = (reason: string) => {
        if (!props?.id) return
        setIsProcessingRefuse(true)
        refuseMutation.mutate({ id: props.id, reason }, {
            onSuccess: () => {
                invalidateTimeOffsQuery();
                invalidateQuery();
                invalidateContractQuery?.();
                setRefuseDialogOpen(false);
            },
        })
    }

  const ticketStatus = useMemo(() => {
    if (props?.dateArchive) {
            return ARCHIVED
    }

        return props?.isClosed ? CLOSED : OPENED
    }, [props])

    const getEndIcon = useCallback((approval) => {
      if (approval?.isApproved) {
            return "fa-circle-check text-success"
      }

      if (approval?.isRefused) {
            return "fa-circle-xmark text-danger"
      }
    }, [props])

    const duration = useMemo(() => {
        if (!props?.dateBegin || !props?.dateEnd) return undefined;
        return props?.countWorkingDays
    }, [props?.dateBegin, props?.dateEnd, props?.countWorkingDays]);

    const isAdmin = (runtimeEnv?.user as any)?.isAdmin
    const hasApproved = props?.approvals?.some((item) => item?.isApproved)
        
    const currentUserDepartmentId = (runtimeEnv?.user as any)?.department?.id
    const employeeDepartmentId = props?.employee?.department?.id
    const isSameDepartment = currentUserDepartmentId === employeeDepartmentId
        
    const canManageRequest = isAdmin || isSameDepartment

  const editable = props.isEditable && !['validated', 'refused'].includes(props?.status)

  const handleDayoffExport = () => {
    exportDayOff.mutate(props?.id?.toString())
  }

  return (
    <div className="flex flex-col gap-7">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col">
          <div className="flex justify-between flex-wrap">
            <h3 className="flex gap-4 items-center">
              <AppointmentDetailsType {...props} />
            </h3>
            <div className="flex gap-2">
              {props.id && (
                <Button
                  className="btn-default"
                  startIcon="fa-print"
                  label={PRINT}
                  onClick={handleDayoffExport}
                  isLoading={exportDayOff.isLoading}
                />
              )}
              {editable && (
                <>
                  <Link to={editLink}>
                    <Button
                      className="btn-default"
                      startIcon="fa-pen"
                      label={EDIT}
                    />
                  </Link>
                  {props.toolbox}
                </>
              )}
            </div>
          </div>
        </div>
        <div className="flex justify-between">
          <Metadata
            label={LABELS}
            value={
              props.labels?.length > 0 ? (
                <div className="flex gap-2 flex-wrap">
                  {props.labels?.map((item, index) => (
                    <Chip
                      key={index}
                      className="bg-white"
                      label={item.value}
                      color={item.color}
                    />
                  ))}
                </div>
              ) : undefined
            }
          />
          <Metadata label={STATUS} value={<VacationStatus {...props} />} />
          <div className="flex gap-5 flex-wrap">
            <Metadata
              label={CREATOR}
              value={
                <Link to={tr(`admin/user/${props?.userCreator?.employee.id}`)}>
                  <Chip
                    userId={props?.userCreator?.id}
                    label={props?.userCreator?.employee?.fullName}
                  />
                </Link>
              }
            />
          </div>
        </div>
      </div>

      <div>
        <div className="flex flex-col gap-2">
          <h3 className="mb-2">{EMPLOYEE_DETAILS}</h3>
          <div className="grid grid-cols-2 gap-2 divide-x-2">
            <div className="px-4">
              {props.employee && (
                <div className="flex items-start gap-2">
                  <Link
                    to={`user/${props?.employee?.id}`}
                    className="flex items-center gap-3"
                  >
                    <Avatar
                      src={userProfilePictureUrl(props?.employee?.id)}
                      fullName={props?.employee?.fullName}
                      size="lg"
                    />
                  </Link>
                  <div className="flex flex-col">
                    <span className="font-medium">
                      {props?.employee?.fullName}
                    </span>
                    <span className="text-xs text-muted">
                      {(props?.employee as any)?.jobPosition?.value ??
                        (props?.employee as any)?.position ??
                        props?.employee?.department?.designation ??
                        ""}
                    </span>
                    <div className="mt-2 flex flex-col">
                      <div className="flex gap-3 items-center">
                        <span className="text-xs text-muted">{POSITION}</span>
                        <span className="font-medium">
                          {(data?.jobPosition as any)?.value ?? "-"}
                        </span>
                      </div>
                      <div className="flex gap-3 items-center">
                        <span className="text-xs text-muted">{REMAINDER}</span>
                        <span className="font-medium">{remainingSolde}</span>
                      </div>
                      {props?.type?.designation && dailyQuotaTypes.includes(props.type.designation) && (
                        <div className="flex gap-3 items-center">
                          <span className="text-xs text-muted">{ANNUAL_REMAINING_QUOTA}</span>
                          <span className="font-medium">
                            {(data?.leaveBalance as any)?.getRemainingQuota ?? 0}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="flex flex-col gap-4 px-4">
              {props.createdAt && (
                <Metadata
                  label={DEMAND_DATE}
                  className="grid grid-cols-3 gap-20"
                  LabelProps={{ className: "col-span-1" }}
                  ValueProps={{ className: "col-span-2" }}
                  value={
                    <div className="flex items-center gap-2">
                      <Chip
                        startIcon="fa-clock"
                        className="bg-default"
                        label={<DateSpan value={new Date(parseInt(props.createdAt))} format="LL" />}
                      />
                    </div>
                  }
                />
              )}
              {props.dateBegin && (
                <Metadata
                  label={DATE_BEGIN}
                  className="grid grid-cols-3 gap-20"
                  LabelProps={{ className: "col-span-1" }}
                  ValueProps={{ className: "col-span-2" }}
                  value={
                    <div className="flex items-center gap-2">
                      <Chip
                        startIcon="fa-clock"
                        className="bg-default"
                        label={<DateSpan value={props.dateBegin} format="LL" />}
                      />
                      {(props.hourBegin || props.hourEnd) && (
                        <span className="text-sm text-muted">
                          {props.hourBegin}
                        </span>
                      )}
                    </div>
                  }
                />
              )}

              {props.dateEnd && (
                <Metadata
                  label={DATE_END}
                  className="grid grid-cols-3 gap-20"
                  LabelProps={{ className: "col-span-1" }}
                  ValueProps={{ className: "col-span-2" }}
                  value={
                    <div className="flex items-center gap-2">
                      <Chip
                        startIcon="fa-clock"
                        className="bg-default"
                        label={<DateSpan value={props.dateEnd} format="LL" />}
                      />
                      {props.hourEnd && (
                        <span className="text-sm text-muted">
                          {props.hourEnd}
                        </span>
                      )}
                    </div>
                  }
                />
              )}
              {duration !== undefined && (
                  <Metadata
                      label={TOTAL_DAYS}
                      className="grid grid-cols-3 gap-20"
                      LabelProps={{ className: "col-span-1" }}
                      ValueProps={{ className: "col-span-2" }}
                      value={<span className="font-medium">{duration.toString()}</span>}
                  />
              )}
            </div>
          </div>

          <div className="flex gap-4 flex-wrap">
            {props.approvals?.length > 0 && (
              <Metadata
                label={USERS}
                className="flex-col items-start"
                value={
                  <div className="flex gap-2 flex-wrap">
                    {props.approvals.map((item) => (
                      <Link to={`user/${item?.approver?.id}`}>
                        <Chip
                          userId={item?.approver?.id}
                          label={item?.approver?.fullName}
                          endIcon={getEndIcon(item)}
                        />
                      </Link>
                    ))}
                  </div>
                }
              />
            )}
          </div>

          <div className="flex gap-4">
            {props.createdAt && (
              <Metadata
                icon="fa-clock"
                value={<DateSpan value={props.createdAt} />}
                title={CREATED_AT}
              />
            )}
          </div>
        </div>
      </div>

      {props.reason && (
        <div className="flex flex-col gap-2">
          <h3>{REASON}</h3>
          <div className="text-sm text-muted break-words">{props.reason}</div>
        </div>
      )}

      {(props as any).refusalReason && (
        <div className="flex flex-col gap-2">
          <h3>{REFUSAL_REASON}</h3>
          <div className="text-sm text-muted break-words">{(props as any).refusalReason}</div>
        </div>
      )}

      {props.files?.length > 0 && (
        <div className="flex flex-col gap-2">
          <h3>{ATTACHMENT}</h3>

          <div className="flex gap-4 flex-wrap">
            {props.files?.map((item, index) => (
              <a href={fileUrl(item.id)} target="_blank">
                <FileItem {...item} key={index} />
              </a>
            ))}
          </div>
        </div>
      )}

      {
        canManageRequest && (
          <div className="flex justify-between gap-2">
            <Button
              className="btn btn-danger !text-white"
              startIcon="fa-circle-xmark"
              label={REFUSE}
              onClick={handleRefuse}
              disabled={
                (props?.status === "approved" && !isAdmin) ||
                props?.status === "refused" ||
                props?.status === "validated" ||
                !props?.id ||
                isProcessingRefuse
              }
            />
            <RefuseDayOffDialog
              open={refuseDialogOpen}
              onClose={() => setRefuseDialogOpen(false)}
              onConfirm={handleRefuseConfirm}
            />
            <Button
              className="btn !bg-success !text-white"
              startIcon="fa-circle-check"
              label={(runtimeEnv?.user as any)?.isAdmin ? VALIDATE : APPROVE}
              onClick={handleApproveOrValidate}
              disabled={
                props?.status === "refused" ||
                props?.status === "validated" ||
                (!isAdmin && props?.status === "approved") ||
                (isAdmin && !hasApproved) ||
                !props?.id ||
                isProcessingApprove
              }
            />
          </div>
        )
      }

      <GenericSuspense>
        <Outlet />
      </GenericSuspense>
    </div>
  );
};

type Props = Partial<TTimeOff> & {
  toolbox?: ReactNode;
  disableDetailsQuery?: boolean;
};

const i18n = [
  "std_creator",
  "std_status",
  "std_closed",
  "std_opened",
  "std_created_at",
  "BSMRH.Project.Dayoff.Demand.Date",
  "BSMRH.Calendar.TimeOff.Create.Form.Label.DateAppointmentBegin",
  "BSMRH.Calendar.TimeOff.Create.Form.Label.DateAppointmentEnd",
  "std_label",
  "Harea.Project.Calendar.Details.Users.Label",
  "Harea.Project.Calendar.Details.Description.Label",
  "std_justification",
  "std_metadata",
  "std_archived",
  "std_edit",
  "Harea.Project.Calendar.Details.Milestone.Label",
  "std_user",
  "Harea.Vacation.Calendar.Event.Create.Vacation.Form.Label.Repetition",
  "std_validate",
  "std_approve",
  "std_refuse",
  "std_print",
  "std_employee",
  "std_position",
  "std_solde_remainder",
  "std_total_days",
  "std_reason",
  "std_refusal_reason",
  "BSMRH.Project.Dayoff.Demand.Details.EmployeeDetails.Label",
  "std_total_hours",
  "std_remaining_annual_quota"
];
